// components/ExtractionModal.js
import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { modifyAIResults } from '../api/aiKeyPointsApi';
import styles from './ExtractionModal.module.css';

const EXTRACTION_TYPES = [
  { key: "keyPoints", title: "Key Points", description: "Brief, clear summaries of the main points" },
  { key: "detailedInsights", title: "Insights", description: "Deeper analysis with context and implications" },
  { key: "biasDetection", title: "Bias Detection", description: "Analysis of potential bias, assumptions, or slanted perspectives" },
  { key: "quotes", title: "Quotes", description: "Notable direct quotes from the text" },
  { key: "factsClaims", title: "Facts & Claims", description: "Specific factual statements or claims made in the text" },
  { key: "completeThoughts", title: "Complete Thoughts", description: "Full, self-contained ideas that provide comprehensive understanding" }
];

const MAX_SELECTIONS = 3;

const ExtractionModal = ({ onClose, onExtract, onCreateStickies, isProcessing }) => {
  const [position, setPosition] = useState({ 
    x: window.innerWidth / 2 - 350, 
    y: window.innerHeight / 2 - 250 
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const [promptText, setPromptText] = useState('');
  const [isModifying, setIsModifying] = useState(false);
  
const [size, setSize] = useState({ width: 700, height: 600 });
const [isResizing, setIsResizing] = useState(false);
  // Modal state
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const handleCheckboxChange = (key) => {
    if (selectedTypes.includes(key)) {
      setSelectedTypes(prev => prev.filter(type => type !== key));
    } else {
      if (selectedTypes.length < MAX_SELECTIONS) {
        setSelectedTypes(prev => [...prev, key]);
      }
    }
  };

  const handleExtract = async () => {
    const result = await onExtract({ extractionTypes: selectedTypes });
    console.log('Extraction result:', result);
    setResults(result);
    setShowResults(true);
  };

  const handleCreateStickies = async () => {
    const stickyResult = await onCreateStickies(results.data, results.extractionTypes);
  };

  const handleAISubmit = async () => {
    if (!promptText.trim()) return;
    
    setIsModifying(true);
    
    try {
      const modifiedResults = await modifyAIResults(
        results.data,
        promptText.trim(),
        results.extractionTypes
      );
      
      setResults({
        ...results,
        data: modifiedResults
      });
      
      setPromptText('');
      
    } catch (error) {
      console.error('Error modifying results:', error);
      alert('Failed to modify results. Please try again.');
    } finally {
      setIsModifying(false);
    }
  };

  const handleResizeStart = (e) => {
  e.preventDefault();
  e.stopPropagation();
  setIsResizing(true);
  
  const startX = e.clientX;
  const startY = e.clientY;
  const startWidth = size.width;
  const startHeight = size.height;

  const handleMouseMove = (moveEvent) => {
    setSize({
      width: Math.max(400, startWidth + (moveEvent.clientX - startX)),
      height: Math.max(300, startHeight + (moveEvent.clientY - startY))
    });
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

  return (
    <div
      className={styles.modalContainer}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,    // ADD THIS LINE
        height: size.height   // ADD THIS LINE
      }}
    >
      {/* Header/Drag Bar */}
      <div className={styles.header} onMouseDown={handleMouseDown}>
        <h3 className={styles.headerTitle}>Select Extraction Types</h3>
        <button className={styles.closeButton} onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {!showResults && (
          <>
            <p className={styles.instructions}>Select up to {MAX_SELECTIONS} types of information to extract:</p>
            
            <div className={styles.optionsContainer}>
              {EXTRACTION_TYPES.map(type => (
                <div key={type.key} className={styles.option}>
                  <label className={styles.optionLabel}>
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type.key)}
                      onChange={() => handleCheckboxChange(type.key)}
                      disabled={!selectedTypes.includes(type.key) && selectedTypes.length >= MAX_SELECTIONS}
                      className={styles.optionCheckbox}
                    />
                    <div>
                      <span className={styles.optionTitle}>{type.title}</span>
                      <p className={styles.optionDescription}>
                        {type.description}
                      </p>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Overlay for processing/results */}
        {(isProcessing || isModifying || showResults) && (
          <div className={styles.overlay}>
            {(isProcessing || isModifying) ? (
              <div className={styles.processingState}>
                <div className={styles.processingIcon}>
                  <svg viewBox="0 0 100 100" width="40" height="40" className={styles.processingSpinner}>
                    <path
                      className={styles.processingPath}
                      d="M50 15L50 85M15 50L85 50M26 26L74 74M26 74L74 26"
                      stroke="#4f46e5"
                      strokeWidth="6"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div>{isProcessing ? 'Processing selected extractions...' : 'Modifying results...'}</div>
              </div>
            ) : results ? (
              <div className={styles.resultsState}>
                <h4 className={styles.resultsTitle}>✅ Extraction Complete!</h4>
                
                {results.data && (
                  <div className={styles.resultsContainer}>
                    {results.extractionTypes.map(type => {
                      const items = results.data[type];
                      if (!items || items.length === 0) return null;
                      
                      const typeTitle = type === 'keyPoints' ? 'Key Points' :
                                       type === 'detailedInsights' ? 'Insights' :
                                       type === 'quotes' ? 'Quotes' :
                                       type === 'biasDetection' ? 'Bias Detection' :
                                       type === 'factsClaims' ? 'Facts & Claims' :
                                       type === 'completeThoughts' ? 'Complete Thoughts' : type;
                      
                      return (
                        <div key={type} className={styles.extractionSection}>
                          <h5 className={styles.extractionTitle}>
                            {typeTitle}:
                          </h5>
                          <ol className={styles.extractionList}>
                            {items.map((item, index) => (
                              <li key={index} className={styles.extractionItem}>
                                {item}
                              </li>
                            ))}
                          </ol>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        {!showResults ? (
          // STATE 1: Selection Phase
          <div className={styles.footerButtons}>
            <button 
              className={`${styles.button} ${styles.buttonCancel}`}
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              className={`${styles.button} ${styles.buttonPrimary} ${(selectedTypes.length === 0 || isProcessing) ? styles.buttonDisabled : ''}`}
              onClick={handleExtract}
              disabled={selectedTypes.length === 0 || isProcessing}
            >
              {isProcessing ? 'Extracting...' : 'Extract Selected Types'}
            </button>
          </div>
        ) : (
          // STATE 2: Results Phase
          <>
            <input
              type="text"
              placeholder="Enter prompt to modify results..."
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              disabled={isModifying}
              className={styles.promptInput}
            />
            
            <div className={styles.footerButtons}>
              <button 
                className={`${styles.button} ${styles.buttonCancel}`}
                onClick={() => {
                  setShowResults(false);
                  setResults(null);
                }}
              >
                Close
              </button>
              <button 
                className={`${styles.button} ${styles.buttonWarning} ${(!promptText.trim() || isModifying) ? styles.buttonDisabled : ''}`}
                onClick={handleAISubmit}
                disabled={!promptText.trim() || isModifying}
              >
                {isModifying ? 'Modifying...' : 'AI Submit'}
              </button>
              <button 
                className={`${styles.button} ${styles.buttonSuccess}`}
                onClick={handleCreateStickies}
              >
                Create as Stickies
              </button>
            </div>
            
          </>
          
        )}
        
        
      </div>
      

      <div 
        className={styles.resizeHandle} 
        onMouseDown={handleResizeStart}
      />
    </div>
  );
  
};

export default ExtractionModal;